import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [originalAdmin, setOriginalAdmin] = useState(null); // Przechowuj oryginalnego super admina podczas impersonacji

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const originalToken = localStorage.getItem('originalAdminToken');
      
      if (token) {
        const userData = await authService.getCurrentUser();
        setUser(userData.user);
        
        // NIE usuwaj originalAdminToken przy odświeżeniu – pozwala to użytkownikowi
        // wrócić do konta super admina przyciskiem "Powrót do super admina"
      } else {
        // Jeśli nie ma tokenu, wyczyść wszystko
        localStorage.removeItem('originalAdminToken');
        setOriginalAdmin(null);
      }
    } catch (error) {
      console.error('Błąd sprawdzania statusu autentykacji:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('originalAdminToken');
      setUser(null);
      setOriginalAdmin(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      // Wyczyść stan impersonacji przed logowaniem
      localStorage.removeItem('originalAdminToken');
      setOriginalAdmin(null);
      
      const response = await authService.login(email, password);
      const { token, user: userData } = response;
      
      localStorage.setItem('token', token);
      setUser(userData);
      
      toast.success('Logowanie pomyślne!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Wystąpił błąd podczas logowania';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (username, email, password) => {
    try {
      setLoading(true);
      const response = await authService.register(username, email, password);
      const { token, user: userData } = response;
      
      localStorage.setItem('token', token);
      setUser(userData);
      
      toast.success('Rejestracja pomyślna!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Wystąpił błąd podczas rejestracji';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Wyślij request do API o wylogowanie
      await authService.logout();
    } catch (error) {
      console.error('Błąd wylogowania:', error);
      // Kontynuuj wylogowanie nawet jeśli API nie działa
    }
    
    // Wyczyść wszystkie tokeny i stan impersonacji
    localStorage.removeItem('token');
    localStorage.removeItem('originalAdminToken');
    setUser(null);
    setOriginalAdmin(null);
    toast.success('Wylogowano pomyślnie');
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await authService.updateProfile(profileData);
      setUser(response.user);
      toast.success('Profil został zaktualizowany');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Wystąpił błąd podczas aktualizacji profilu';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await authService.changePassword(currentPassword, newPassword);
      toast.success('Hasło zostało zmienione');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Wystąpił błąd podczas zmiany hasła';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const refreshUser = useCallback(async () => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData.user);
    } catch (error) {
      console.error('Błąd odświeżania danych użytkownika:', error);
      // Jeśli błąd 401, użytkownik nie jest zalogowany
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('originalAdminToken');
        setUser(null);
        setOriginalAdmin(null);
      }
    }
  }, []);

  const impersonate = async (userId) => {
    try {
      setLoading(true);
      // Zapisz oryginalny token super admina
      const originalToken = localStorage.getItem('token');
      if (originalToken && user?.role === 'super admin') {
        localStorage.setItem('originalAdminToken', originalToken);
        setOriginalAdmin(user);
      }

      const response = await authService.impersonate(userId);
      const { token, user: userData, impersonatedBy } = response;
      
      localStorage.setItem('token', token);
      setUser(userData);
      
      toast.success(`Zalogowano jako ${userData.username || userData.firstName + ' ' + userData.lastName}`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Błąd podczas logowania jako inny użytkownik';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const stopImpersonating = async () => {
    try {
      setLoading(true);
      const originalToken = localStorage.getItem('originalAdminToken');
      
      if (!originalToken) {
        // Jeśli nie ma zapisanego tokenu, po prostu wyloguj
        await logout();
        return { success: false };
      }

      // Przywróć oryginalny token
      localStorage.setItem('token', originalToken);
      localStorage.removeItem('originalAdminToken');
      
      // Wyczyść stan impersonacji PRZED pobraniem danych
      setOriginalAdmin(null);
      
      // Pobierz świeże dane super admina używając przywróconego tokenu
      const userData = await authService.getCurrentUser();
      setUser(userData.user);
      
      toast.success('Powrócono do konta super administratora');
      return { success: true };
    } catch (error) {
      console.error('Błąd powrotu do konta super admina:', error);
      // Jeśli błąd, wyczyść wszystko i wyloguj
      localStorage.removeItem('token');
      localStorage.removeItem('originalAdminToken');
      setUser(null);
      setOriginalAdmin(null);
      toast.error('Błąd podczas powrotu do konta super administratora');
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  // Sprawdź czy jesteśmy w trybie impersonacji
  const isImpersonating = !!originalAdmin || !!localStorage.getItem('originalAdminToken');

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    refreshUser,
    impersonate,
    stopImpersonating,
    isImpersonating,
    originalAdmin,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
